import * as React from 'react';
import { ThemeContext } from 'styled-components';
import camelCase from 'lodash/camelCase';

import {
  IMakeErrorMsgID,
  makeErrorMsgID,
  useFocusControl,
  useHoverControl,
  useUniqueId,
  VALID_BORDER_DATA,
  VALID_COLOR_DATA,
  VALID_EDGE_STYLE_DATA,
  VALID_MEDIA_STYLES,
  VALID_OUTLINE_DATA,
  VALID_SHADOW_DATA,
} from '../../Utils';
import { SVGIcon } from '../../../Icon';
import { IErrorMsgs, IInputProps } from '../common';
import { FormError } from '../FormStyles';
import { VALID_GLOBAL_FONT_SIZES } from '../../Typography/common';
import { VALID_ICONS } from '../../../Icon/SVGIcon';
import { SPACEBAR, SPACEBAR_ATL } from '../../Utils/events/keyCodes';
import { useFieldSetGroupContext } from '../FieldSet/FieldSetGroup';

import { CheckboxContain, CheckboxInput, CheckboxLabel } from './InputCheckboxStyles';

export interface ICheckboxCommonTheme {
  border: VALID_BORDER_DATA;
  backgroundColor: VALID_COLOR_DATA;
  icon: {
    checkedIconType: VALID_ICONS;
    size: string;
    color: VALID_COLOR_DATA;
  };
  label: {
    verticalOffset?: VALID_MEDIA_STYLES;
    font: { color: VALID_COLOR_DATA; lineHeight?: string; size?: VALID_GLOBAL_FONT_SIZES };
  };
  outline?: VALID_OUTLINE_DATA;
  shadow?: VALID_SHADOW_DATA;
  margin: { right: string; left?: string };
  padding?: VALID_EDGE_STYLE_DATA;
}

export interface IInputCheckboxField extends IErrorMsgs, IInputProps {
  iconType?: 'mixed' | 'done';
  inputId: string;
  value?: string;
  groupName?: string;
  hasError?: boolean;
}

// exporting here is a work around for this issue https://github.com/microsoft/tslint-microsoft-contrib/issues/667
// tslint:disable-next-line:max-func-body-length
export const InputCheckboxField = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & IInputCheckboxField>((
  {
    children,
    errorMsgs,
    groupName: groupNameComponent,
    hasError: hasErrorComponent,
    iconType,
    inputId,
    inputProps,
    value
  },
  ref
) => {
  const { errorIds, groupName, hasError: hasGroupError, helperTextId } = useFieldSetGroupContext();
  const [ isFocused, setFocus ] = React.useState(false);
  const { clickFocus, stopClickFocus, resetClickFocus } = useFocusControl();
  const { isHovered: labelIsHovered, setHovered } = useHoverControl();

  const isGrouped = !!(groupName || groupNameComponent);
  const errorMsgControls: IMakeErrorMsgID | null = errorMsgs ? makeErrorMsgID(errorMsgs, inputId) : null;
  const formattedInputId = useUniqueId(camelCase(inputId));
  const hasError: boolean | undefined = !!errorMsgControls && !isGrouped || hasGroupError;
  const id: string = value ? camelCase(`${value} id`) : formattedInputId;
  const inputErrorWCAG: string | undefined = errorMsgControls && !errorIds ? errorMsgControls.errorMsgIds.join(' ') : errorIds;
  const { themeTwo: { form: { checkbox, errorText: { checkboxAndRadioErrorIndention }}}} = React.useContext(ThemeContext);
  const hasFocusPosition = checkbox.state.focus.focusPosition === 'labelAndIndicator';
  const isDefaultChecked = !!(inputProps && inputProps.defaultChecked);
  const isDisabled = !!(inputProps && inputProps.disabled);

  const [ isInputChecked, setCheckedInput ] = React.useState(isDefaultChecked);

  const toggleIsFocused = () => hasFocusPosition ? setFocus(!isFocused) : undefined;
  const handleOnBlur = () => { resetClickFocus(); toggleIsFocused(); };
  const labelHovered = () => setHovered(true);
  const labelNotHovered = () => setHovered(false);

  // tslint:disable-next-line: no-any
  const handleMouseDown = (e: any) => {
    if (!!e.target.closest('a') || !!e.target.closest('button')) { return; }
    stopClickFocus();

    if (!isDisabled) {
      setCheckedInput(!isInputChecked);
    }
  };

  const handleInputKeydown: (e: React.KeyboardEvent<HTMLInputElement>) => void = e => {
    if (e.key === SPACEBAR || e.key === SPACEBAR_ATL) {
      setCheckedInput(!isInputChecked);
    }
  };

  return (
    <CheckboxContain
      className={hasFocusPosition && isFocused ? 'focus' : undefined}
      clickFocus={clickFocus}
    >
      <CheckboxInput
        /* see above comments for more about the name and groupName prop */
        name={groupName ? groupName : groupNameComponent}
        checked={isInputChecked}
        {...inputProps}
        aria-describedby={inputErrorWCAG ? `${inputErrorWCAG} ${helperTextId && helperTextId}` :  helperTextId}
        data-id={id}
        iconType={iconType}
        ref={ref}
        type='Checkbox'
        value={value}
        error={hasError || hasErrorComponent}
        onFocus={toggleIsFocused}
        onBlur={handleOnBlur}
        onKeyDown={handleInputKeydown}
        clickFocus={clickFocus}
        onMouseDown={handleMouseDown}
        aria-labelledby={id ? id : undefined}
        isHovered={labelIsHovered}
      />
      <CheckboxLabel
        data-id={id}
        id={id}
        onMouseDown={handleMouseDown}
        onMouseEnter={labelHovered}
        onMouseLeave={labelNotHovered}
      >
        {children}
      </CheckboxLabel>
      <SVGIcon icon={iconType === 'mixed' ? 'minus' : checkbox.icon.checkedIconType} />

      {/* if the groupName prop is used then error msgs are handled within the parent */}
      {errorMsgControls && !groupName && (
        errorMsgControls.errorMessages.map((msg, index) => (
          <FormError
            canIndentError={!!checkboxAndRadioErrorIndention}
            id={errorMsgControls.errorMsgIds[index]}
            key={msg}
          >
            {msg}
          </FormError>
        )
      ))}
    </CheckboxContain>
  );
});

InputCheckboxField.displayName = 'InputCheckboxField';

export default InputCheckboxField;
